import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { thousandize } from "../../../../util/formatting";
interface BarChartProps {
  donation: number;
  maximumDeduction: number;
  taxBenefit: number;
  labels?: {
    maximumDeduction?: string;
    yourDonation?: string;
    yourTaxBenefit?: string;
  };
  locale?: string;
}
interface Dimensions {
  width: number;
  height: number;
}
interface Padding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
interface LabelPosition {
  translateX: number;
  alignmentOffset: number;
}
export const HorizontalDKGavebrevTaxBarChart: React.FC<BarChartProps> = t0 => {
  const $ = _c(87);
  const {
    donation,
    maximumDeduction,
    taxBenefit,
    labels,
    locale
  } = t0;
  const containerRef = useRef(null);
  const labelRef = useRef(null);
  const sublabelRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: 0,
      height: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dimensions, setDimensions] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!containerRef.current) {
        return;
      }
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const {
            width,
            height
          } = entry.contentRect;
          setDimensions({
            width,
            height
          });
        }
      });
      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const currentValue = Math.min(donation, maximumDeduction);
  const [maxLabelWidth, setMaxLabelWidth] = useState(0);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!labelRef.current || !sublabelRef.current) {
        return;
      }
      const labelWidth = labelRef.current.getBBox().width;
      const sublabelWidth = sublabelRef.current.getBBox().width;
      setMaxLabelWidth(Math.max(labelWidth, sublabelWidth));
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = labels?.yourDonation;
  let t6;
  if ($[4] !== currentValue || $[5] !== dimensions.width || $[6] !== locale || $[7] !== t5) {
    t6 = [dimensions.width, currentValue, t5, locale];
    $[4] = currentValue;
    $[5] = dimensions.width;
    $[6] = locale;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useLayoutEffect(t4, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      left: 0,
      right: 10,
      top: 50,
      bottom: 0
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const padding = t7;
  const chartWidth = dimensions.width - padding.left - padding.right;
  let t8;
  if ($[10] !== chartWidth || $[11] !== maximumDeduction) {
    t8 = val => {
      if (maximumDeduction === 0) {
        return 0;
      }
      return val / maximumDeduction * chartWidth;
    };
    $[10] = chartWidth;
    $[11] = maximumDeduction;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const scale = t8;
  let t9;
  if ($[13] !== currentValue || $[14] !== dimensions.width || $[15] !== maxLabelWidth || $[16] !== scale) {
    const getLabelPositions = () => {
      if (!dimensions.width) {
        return {
          translateX: 0,
          alignmentOffset: 0
        };
      }
      const basePosition = scale(currentValue);
      const maxPosition = dimensions.width - padding.right;
      const wouldOverflow = basePosition + maxLabelWidth + 6 > maxPosition;
      if (wouldOverflow) {
        const alignmentOffset = maxLabelWidth;
        return {
          translateX: maxPosition,
          alignmentOffset
        };
      }
      return {
        translateX: basePosition,
        alignmentOffset: 0
      };
    };
    t9 = getLabelPositions();
    $[13] = currentValue;
    $[14] = dimensions.width;
    $[15] = maxLabelWidth;
    $[16] = scale;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const labelPosition = t9;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      width: "100%",
      position: "relative",
      fontSize: "0.9rem"
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = dimensions.width;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <defs><pattern id="mobileDiagonalHatch" patternUnits="userSpaceOnUse" width="4" height="4"><path d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2" style={{
          stroke: "#fafafa",
          strokeWidth: 1
        }} /></pattern></defs>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = dimensions.width - padding.right;
  let t14;
  if ($[20] !== t13) {
    t14 = <line x1={padding.left} y1={padding.top} x2={t13} y2={padding.top} stroke="black" strokeWidth="1" />;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = dimensions.width - padding.right;
  let t16;
  if ($[22] !== t15) {
    t16 = <line x1={padding.left} y1={padding.top + 40} x2={t15} y2={padding.top + 40} stroke="black" strokeWidth="1" />;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = donation > 0 ? 1 : 0;
  let t18;
  if ($[24] !== t17) {
    t18 = {
      transition: "all 0.5s",
      opacity: t17
    };
    $[24] = t17;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  const t19 = `translateX(${scale(currentValue) - 0.5}px)`;
  let t20;
  if ($[26] !== t19) {
    t20 = {
      transform: t19,
      transition: "all 0.5s"
    };
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      transition: "all 0.5s"
    };
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  const t22 = labelPosition.alignmentOffset > 0 ? 0 : 1;
  let t23;
  if ($[29] !== t22) {
    t23 = <line x1={0} y1={padding.top - 5} x2={0} y2={padding.top} stroke="black" strokeWidth="1" style={t21} opacity={t22} />;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== t20 || $[32] !== t23) {
    t24 = <g style={t20}>{t23}</g>;
    $[31] = t20;
    $[32] = t23;
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  const t25 = `translateX(${labelPosition.translateX}px)`;
  let t26;
  if ($[34] !== t25) {
    t26 = {
      transform: t25,
      transition: "all 0.5s"
    };
    $[34] = t25;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  const t27 = `translateX(${-labelPosition.alignmentOffset}px)`;
  let t28;
  if ($[36] !== t27) {
    t28 = {
      transform: t27,
      transition: "all 0.5s"
    };
    $[36] = t27;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  const t29 = labels?.yourDonation || "Din donation";
  let t30;
  if ($[38] !== t28 || $[39] !== t29) {
    t30 = <text ref={labelRef} x={0} y={padding.top - 30} textAnchor="start" alignmentBaseline="middle" style={t28}>{t29}</text>;
    $[38] = t28;
    $[39] = t29;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  const t31 = `translateX(${-labelPosition.alignmentOffset}px)`;
  let t32;
  if ($[41] !== t31) {
    t32 = {
      transform: t31,
      transition: "all 0.5s"
    };
    $[41] = t31;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  let t33;
  if ($[43] !== currentValue || $[44] !== locale) {
    t33 = thousandize(currentValue, locale);
    $[43] = currentValue;
    $[44] = locale;
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== t32 || $[47] !== t33) {
    t34 = <text ref={sublabelRef} x={0} y={padding.top - 15} textAnchor="start" alignmentBaseline="middle" fontSize="0.75rem" style={t32}>{t33} kr</text>;
    $[46] = t32;
    $[47] = t33;
    $[48] = t34;
  } else {
    t34 = $[48];
  }
  let t35;
  if ($[49] !== t26 || $[50] !== t30 || $[51] !== t34) {
    t35 = <g style={t26}>{t30}{t34}</g>;
    $[49] = t26;
    $[50] = t30;
    $[51] = t34;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] !== t18 || $[54] !== t24 || $[55] !== t35) {
    t36 = <g style={t18}>{t24}{t35}</g>;
    $[53] = t18;
    $[54] = t24;
    $[55] = t35;
    $[56] = t36;
  } else {
    t36 = $[56];
  }
  const t37 = taxBenefit > 0 ? 1 : 0;
  const t38 = `translateX(${scale(taxBenefit)}px)`;
  let t39;
  if ($[57] !== t37 || $[58] !== t38) {
    t39 = {
      transition: "all 0.5s",
      opacity: t37,
      transform: t38
    };
    $[57] = t37;
    $[58] = t38;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <line x1={0} y1={padding.top + 40} x2={0} y2={padding.top + 40 + 5} stroke="black" strokeWidth="1" />;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  const t41 = labels?.yourTaxBenefit || "Dit fradrag";
  let t42;
  if ($[61] !== t41) {
    t42 = <text x={0} y={padding.top + 40 + 20} textAnchor="start" alignmentBaseline="middle">{t41}</text>;
    $[61] = t41;
    $[62] = t42;
  } else {
    t42 = $[62];
  }
  let t43;
  if ($[63] !== locale || $[64] !== taxBenefit) {
    t43 = thousandize(taxBenefit, locale);
    $[63] = locale;
    $[64] = taxBenefit;
    $[65] = t43;
  } else {
    t43 = $[65];
  }
  let t44;
  if ($[66] !== t43) {
    t44 = <text x={0} y={padding.top + 40 + 36} textAnchor="start" alignmentBaseline="middle" fontSize="0.75rem">{t43} kr</text>;
    $[66] = t43;
    $[67] = t44;
  } else {
    t44 = $[67];
  }
  let t45;
  if ($[68] !== t39 || $[69] !== t42 || $[70] !== t44) {
    t45 = <g style={t39}>{t40}{t42}{t44}</g>;
    $[68] = t39;
    $[69] = t42;
    $[70] = t44;
    $[71] = t45;
  } else {
    t45 = $[71];
  }
  const t46 = padding.left;
  const t47 = padding.top;
  const t48 = scale(Math.min(donation, maximumDeduction));
  const t49 = donation > 0 ? 1 : 0.3;
  let t50;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = {
      transition: "all 0.5s"
    };
    $[72] = t50;
  } else {
    t50 = $[72];
  }
  let t51;
  if ($[73] !== t48 || $[74] !== t49) {
    t51 = <rect x={t46} y={t47} width={t48} height={40} fill="#000" opacity={t49} style={t50} />;
    $[73] = t48;
    $[74] = t49;
    $[75] = t51;
  } else {
    t51 = $[75];
  }
  let t52;
  if ($[76] !== scale || $[77] !== taxBenefit) {
    t52 = taxBenefit > 0 && <rect x={padding.left} y={padding.top} width={scale(taxBenefit)} height={40} fill="url(#mobileDiagonalHatch)" style={{
      transition: "all 0.5s"
    }} />;
    $[76] = scale;
    $[77] = taxBenefit;
    $[78] = t52;
  } else {
    t52 = $[78];
  }
  let t53;
  if ($[79] !== dimensions.width || $[80] !== t14 || $[81] !== t16 || $[82] !== t36 || $[83] !== t45 || $[84] !== t51 || $[85] !== t52) {
    t53 = <div ref={containerRef} style={t10}><svg width={t11} height="auto">{t12}{t14}{t16}{t36}{t45}{t51}{t52}</svg></div>;
    $[79] = dimensions.width;
    $[80] = t14;
    $[81] = t16;
    $[82] = t36;
    $[83] = t45;
    $[84] = t51;
    $[85] = t52;
    $[86] = t53;
  } else {
    t53 = $[86];
  }
  return t53;
};